
import React, { useState, useEffect } from 'react';
import ChannelSettings from './ChannelSettings';
import FrequencyManagement from './FrequencyManagement';
import RecorderDevices from './RecorderDevices';
import USBRecorders from './USBRecorders';
import { Radio, RadioTower, SmartphoneNfc, Usb } from 'lucide-react';

const ChannelsAndStationsSection = ({
  isDarkMode,
  edgeServerEndpoint,
  recordersEnabled,
  globalSettings,
}) => {
  // Determine which tabs should be visible based on global settings
  const edgeRecordersEnabled = String(globalSettings?.enable_edge_devices || 'False').toLowerCase() === 'true';
  const usbRecordersEnabled = String(globalSettings?.enable_usb_audio_devices || 'False').toLowerCase() === 'true';
  
  // Determine initial active tab - prefer first available tab
  const getInitialTab = () => {
    if (edgeRecordersEnabled) return 'recorders';
    if (usbRecordersEnabled) return 'usb-recorders';
    return 'channels'; // Always show channels and stations
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  
  // Update active tab if current tab becomes hidden
  useEffect(() => {
    if (activeTab === 'recorders' && !edgeRecordersEnabled) {
      // Switch to first available tab
      if (usbRecordersEnabled) {
        setActiveTab('usb-recorders');
      } else {
        setActiveTab('channels');
      }
    } else if (activeTab === 'usb-recorders' && !usbRecordersEnabled) {
      // Switch to first available tab
      if (edgeRecordersEnabled) {
        setActiveTab('recorders');
      } else {
        setActiveTab('channels');
      }
    }
  }, [activeTab, edgeRecordersEnabled, usbRecordersEnabled]);

  const tabButtonClasses = (isActive) =>
    `
      flex items-center gap-1 md:gap-2 py-3 md:py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm transition-colors
      ${isActive
        ? isDarkMode
          ? 'border-blue-500 text-blue-400'
          : 'border-blue-600 text-blue-600'
        : isDarkMode
          ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
      }
    `;

  const containerClasses = isDarkMode
    ? 'bg-gray-900/60 border-gray-800'
    : 'bg-white border-gray-200';

  return (
    <div className={`rounded-2xl shadow-lg p-4 md:p-6 transition-colors duration-300 border ${containerClasses}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-2xl shadow-md ${isDarkMode ? 'bg-blue-600' : 'bg-blue-500'}`}>
            <Radio className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Recorders</h2>
            <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Configure channels, frequencies, and connected recorders.
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} overflow-x-auto`}>
        <nav className="flex space-x-4 md:space-x-8 min-w-max" aria-label="Tabs">
          {edgeRecordersEnabled && (
            <button
              onClick={() => setActiveTab('recorders')}
              className={`${tabButtonClasses(activeTab === 'recorders')} flex-shrink-0 whitespace-nowrap`}
            >
              <SmartphoneNfc className="w-4 h-4 md:w-5 md:h-5" />
              Edge Recorders
            </button>
          )}
          {usbRecordersEnabled && (
            <button
              onClick={() => setActiveTab('usb-recorders')}
              className={`${tabButtonClasses(activeTab === 'usb-recorders')} flex-shrink-0 whitespace-nowrap`}
            >
              <Usb className="w-4 h-4 md:w-5 md:h-5" />
              USB Recorders
            </button>
          )}
          <button
            onClick={() => setActiveTab('channels')}
            className={`${tabButtonClasses(activeTab === 'channels')} flex-shrink-0 whitespace-nowrap`}
          >
            <Radio className="w-4 h-4 md:w-5 md:h-5" />
            Channels
          </button>
          <button
            onClick={() => setActiveTab('stations')}
            className={`${tabButtonClasses(activeTab === 'stations')} flex-shrink-0 whitespace-nowrap`}
          >
            <RadioTower className="w-4 h-4 md:w-5 md:h-5" />
            Stations
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-4">
        {activeTab === 'recorders' && (
          <RecorderDevices
            edgeServerEndpoint={edgeServerEndpoint}
            isDarkMode={isDarkMode}
            enabled={recordersEnabled}
          />
        )}
        {activeTab === 'usb-recorders' && (
          <USBRecorders
            edgeServerEndpoint={edgeServerEndpoint}
            isDarkMode={isDarkMode}
            globalSettings={globalSettings}
          />
        )}
        {activeTab === 'channels' && (
          <ChannelSettings
            isDarkMode={isDarkMode}
            edgeServerEndpoint={edgeServerEndpoint}
          />
        )}
        {activeTab === 'stations' && (
          <FrequencyManagement
            edgeServerEndpoint={edgeServerEndpoint}
            isDarkMode={isDarkMode}
          />
        )}
      </div>
    </div>
  );
};

export default ChannelsAndStationsSection;
